import { useCallback, useState } from 'react';
import { verifyLocationPath, changedFields, companyDeleteSubject, deceasedConsequence, fill, personDeleteDeals, personDeleteSubject, editProblems, editTitle, hasChangedFields, isDeceasedTransition, Color, LIST_PAGES, Size, t, visibleEditFields, type CompanyDeletePreviewAnswer, type Consequence, type DeceasedPreviewAnswer, type PagedRecord, type PersonDeletePreviewAnswer } from '@bms2/shared';
import { Button, Dropdown, TextInput } from '../../components/controls';
import { ConfirmationDialog } from '../../components/controls/ConfirmationDialog';
import { FormFields, FormFrame } from '../../components/forms';
import { PAGES_PATH, del, get, post, put, useApi } from './api';
import { LocationVerifyStep, addressFromFields } from './LocationVerify';
import { RecordFields } from './RecordFields';
import { changeRequestDecisionPath } from './ChangeRequests';
import { CompanyOtherNames, otherNamesChanged, saveOtherNames } from './CompanyOtherNames';
import { useSuccessorSearch } from './DealModals';
import type { LocationVerifyAnswer, SiteTypeDefaultAnswers, VerifyChoice } from '@bms2/shared';
import { SiteTypeAnswerWindow, siteTypeChosen, useSiteTypeAsk } from './SiteTypeAnswerWindow';
import { LookUpManufacturerButton } from './LookUpManufacturer';

/**
 * A record page's own field form (piece 13). One construction serves all four
 * records, because the editable fields come from the shared register rather
 * than from the screen. 🔨 A field absent from that register is not editable
 * here: a custom field is written through its own tile, a tag through the Flags
 * tile, and a dated link is closed and opened rather than edited in place. 🔒 A
 * Company's other names are the one group drawn beside the register, saved whole
 * through their own route before the fields (decision 7150).
 */
export interface EditRecordModalProps {
  open: boolean;
  recordType: PagedRecord;
  recordId: string;
  /** The values the page already read, so the form opens on what is there. */
  initial: Readonly<Record<string, unknown>>;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
  /** The Company was deleted: the page leaves for the list, because the record is in the trash now (piece 12). */
  onDeleted?: () => void;
  openerId?: string | null;
  /**
   * 🔒 Decision 0971: a change request being accepted. The form opens with the
   * named field already holding the request's words, and the request itself
   * stands above the fields so the decider corrects what was asked, not what
   * they guess was asked. Delete is not offered from it.
   * 🔒 The save goes to the request's own Accept, never the record's Save route
   * (T355): the account owner decides without write on the Company module, and
   * where they hold none the form draws only `fields`, the ones the Accept writes.
   */
  request?: { id: string; prefill: Readonly<Record<string, unknown>>; notice: string; fields?: readonly string[] | null } | null;
  /**
   * 🔒 Decision 7460: Use This Address opens a Location's form with the address a service placed already typed in, for the person to check.
   * Unlike a change request's prefill it is the person's own draft, so Save writes it through the record's route.
   */
  prefill?: Readonly<Record<string, unknown>> | null;
  /** 🔒 Decision 7460: the save asks the services about the address again, as Use This Address promises. */
  verifyOnSave?: boolean;
}

export function EditRecordModal({ open, recordType, recordId, initial, apiBaseUrl = '', onCancel, onSaved, onDeleted, openerId = null, request = null, prefill = null, verifyOnSave = false }: EditRecordModalProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...(request?.prefill ?? {}), ...(prefill ?? {}) }));
  const [error, setError] = useState<string | null>(null);
  /** A Deal value below the floor is offered as an override that collects the required reason, never a flat refusal (decision 0048). */
  const [floorRefused, setFloorRefused] = useState(false);
  const [floorReason, setFloorReason] = useState('');
  /** The Location form's Verify step: what its answer lands on the record once the save holds (decision 0338). */
  const [landing, setLanding] = useState<{ outcome: LocationVerifyAnswer['outcome']; choice: VerifyChoice | null } | null>(null);
  /** The Deceased confirm (decision 0169): the blast radius the preview answered, held until the person confirms or cancels. */
  const [deceased, setDeceased] = useState<Consequence | null>(null);
  /** 🔒 Decision 7150: the Company's other names as drafted, null until a line moves; a change request draws none. */
  const [otherNames, setOtherNames] = useState<string[] | null>(null);
  const namesHeld = Array.isArray(initial.otherNames) ? (initial.otherNames as unknown[]).filter((name): name is string => typeof name === 'string') : [];
  const drawsOtherNames = recordType === 'CompanyRecord' && request === null;
  const namesMoved = drawsOtherNames && otherNames !== null && otherNamesChanged(namesHeld, otherNames);
  /** The Company delete's counts (piece 12), read as the form opens so the typed-phrase dialog names them; without them Delete is not offered. */
  const deletePreview = useApi<CompanyDeletePreviewAnswer>(open && recordType === 'CompanyRecord' && request === null ? `${PAGES_PATH}/companies/${recordId}/delete-preview` : null, apiBaseUrl);
  /** 🔒 Decision 7710: the Person delete's counts and the sentence that refuses it, read as the form opens; without the counts Delete is not offered. */
  const personPreview = useApi<PersonDeletePreviewAnswer>(open && recordType === 'PersonRecord' && request === null ? `${PAGES_PATH}/people/${recordId}/delete-preview` : null, apiBaseUrl);
  /** 🔒 Decision 9740: who takes the person's open Deals, chosen from the people employed here before the typed phrase. */
  const [successor, setSuccessor] = useState<{ id: string; name: string } | null>(null);
  const staffSearch = useSuccessorSearch(apiBaseUrl, recordId);
  /** 🔒 Decision 10210: a Site's type change reads the type's offer first, and asks Overwrite or Keep where a set setting differs. */
  const typeAsk = useSiteTypeAsk(apiBaseUrl);
  const remove = useCallback(async () => {
    const path = `${PAGES_PATH}/records/${recordType === 'PersonRecord' ? 'PersonRecord' : 'CompanyRecord'}/${recordId}`;
    const answer = await del(path, apiBaseUrl, recordType === 'PersonRecord' && successor ? { successorEmploymentId: successor.id } : undefined);
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onCancel();
    onDeleted?.();
  }, [apiBaseUrl, onCancel, onDeleted, recordId, recordType, successor]);
  // A closed form holds no draft and no banner, both derived from `open`.
  const shownError = open ? error : null;
  const read = (key: string): unknown => (key in values ? values[key] : initial[key]);
  const set = (key: string, value: unknown) => setValues((held) => ({ ...held, [key]: value }));
  // 🔨 A create-only field, such as a Deal's origin, is never on the Edit form (decision 0353).
  const only = request?.fields ?? null;
  const allFields = visibleEditFields(recordType, 'edit', read);
  const fields = only === null ? allFields : allFields.filter((field) => only.includes(field.key));

  const save = useCallback(async (confirmed = false, typeAnswers: SiteTypeDefaultAnswers | null = null) => {
    setError(null);
    const merged = Object.fromEntries(fields.map((f) => [f.key, key(f.key, values, initial)]));
    // A field the form does not draw keeps the record's own value, and is judged on it.
    const missing = editProblems(recordType, Object.fromEntries(allFields.map((f) => [f.key, key(f.key, values, initial)])));
    if (missing.length > 0) {
      setError(`${t('edit.required')} ${missing.join(', ')}`);
      return;
    }
    // 🔨 The move into Deceased is confirmed first and names its counts: this will notify N people and end M employments (decision 0169).
    if (!confirmed && isDeceasedTransition(recordType, initial.status, merged.status)) {
      const preview = await get<DeceasedPreviewAnswer>(`${PAGES_PATH}/people/${recordId}/deceased-preview`, apiBaseUrl);
      if (!preview.ok) {
        setError(`${t('deceased.previewFailed')} ${preview.message}`);
        return;
      }
      setDeceased(deceasedConsequence(preview.data));
      return;
    }
    /**
     * 🔒 Save sends only the fields whose value differs from what the page read (decision 3062), so the history names
     * only those and a second person's change to an untouched field stands. A change request's Accept always carries
     * the fields the request asked for, which are what it accepts.
     */
    // 🔒 Decision 7150: the other names save first, so a refused name leaves the record's fields unwritten too.
    if (namesMoved && otherNames) {
      const named = await saveOtherNames(recordId, otherNames, apiBaseUrl);
      if (!named.ok) {
        setError(named.message);
        return;
      }
    }
    const changed = changedFields(initial, merged);
    const asked = request ? Object.fromEntries(Object.keys(request.prefill).filter((name) => name in merged).map((name) => [name, merged[name]])) : {};
    const sent = { ...changed, ...asked };
    const body = recordType === 'Deal' && floorReason.trim() ? { ...sent, floorOverrideReason: floorReason.trim() } : sent;
    // 🔨 A change request whose asked-for fields are none of the drawn ones has nothing to accept: it says so and stays Pending, never closes as if it were decided (T2248).
    if (request && Object.keys(body).length === 0) {
      setError(t('edit.requestNotApplied'));
      return;
    }
    // 🔒 Decision 10210: a type chosen or changed on a Site asks before its defaults overwrite a set setting; the answers ride the Save.
    const chosenType = recordType === 'SiteRecord' && !request && typeAnswers === null ? siteTypeChosen(initial.siteTypeId, body.siteTypeId) : null;
    if (chosenType) {
      const asked = await typeAsk.check(recordId, chosenType, typeof merged.isTaxExempt === 'boolean' ? merged.isTaxExempt : undefined);
      if (asked === 'ask') return;
      if (asked) {
        setError(asked);
        return;
      }
    }
    const written = typeAnswers ? { ...body, applyDefaults: typeAnswers } : body;
    // 🔨 Nothing changed is nothing to write: no request is sent and no history row is made.
    const answer = Object.keys(written).length === 0
      ? ({ ok: true } as const)
      // 🔒 A change request's Accept saves through the request's own gate, which also marks it Accepted (T355).
      : request ? await post(changeRequestDecisionPath(request.id, 'accept'), { edit: body }, apiBaseUrl) : await put(`${PAGES_PATH}/records/${recordType}/${recordId}`, written, apiBaseUrl);
    if (!answer.ok) {
      if (recordType === 'Deal' && /floor/i.test(answer.message)) setFloorRefused(true);
      setError(answer.message);
      return;
    }
    // The verified answer lands once the record holds the address it was given for; the cache makes the second call free.
    if (recordType === 'LocationRecord' && landing && (landing.outcome !== 'Corrected' || landing.choice)) {
      await post(verifyLocationPath(recordId), landing.choice ? { choice: landing.choice } : {}, apiBaseUrl);
    } else if (recordType === 'LocationRecord' && verifyOnSave) {
      // 🔒 Use This Address: the saved address is asked about again; a refusal here leaves it saved and unverified (decision 7460).
      await post(verifyLocationPath(recordId), {}, apiBaseUrl);
    }
    onSaved();
    onCancel();
  }, [allFields, apiBaseUrl, fields, floorReason, initial, landing, namesMoved, onCancel, onSaved, otherNames, recordId, recordType, request, typeAsk, values, verifyOnSave]);

  /** 🔒 What the leave guard reads (decision 3061): a field moved from what the window opened on, a change request's own words aside. */
  const typed = open && (hasChangedFields({ ...initial, ...(request?.prefill ?? {}), ...(prefill ?? {}) }, values) || floorReason.trim() !== '' || namesMoved);
  const deletes = recordType === 'CompanyRecord' && deletePreview.state.kind === 'ready' ? deletePreview.state.data : null;
  const personFacts = recordType === 'PersonRecord' && personPreview.state.kind === 'ready' ? personPreview.state.data : null;
  // A preview missing its counts offers no Delete, as the Company's does.
  const personDeletes = personFacts && !personFacts.refusal && typeof personFacts.employments === 'number' ? personFacts : null;
  // 🔒 Decision 9740: open Deals ask a successor, and Delete person stays greyed, saying why, until one is chosen.
  const openDeals = personDeletes?.openDeals ?? 0;
  const awaitsSuccessor = openDeals > 0 && successor === null;

  return (
    // 🔒 Drawn through the Form frame (decision 3060): the refusal reads inside it, and Escape, the × and Cancel ask before typing is dropped.
    <FormFrame
      host="window"
      open={open}
      title={editTitle(LIST_PAGES[recordType].singular)}
      onCancel={onCancel}
      openerId={openerId}
      dataForm="edit-record"
      typed={typed}
      refusal={shownError}
      onDismissRefusal={() => setError(null)}
      act={{ label: t('edit.save'), onAct: () => save(false) }}
      // 🔨 Delete sits at the footer's far left on an existing Company and names what is linked: sites and employments end, open Deals stay (piece 12).
      {...(deletes ? { secondary: { label: t('companyDelete.action'), onAct: () => void remove(), destructive: { subject: companyDeleteSubject(deletes), phrase: t('modal.deletePhrase') } } } : {})}
      // 🔨 Decision 7710: the same footer slot on a Person, naming the employments and logins that end and the approvals and assignments that stay; a refusal replaces the button with its sentence.
      {...(personDeletes ? { secondary: { label: t('personDelete.action'), onAct: () => void remove(), destructive: { subject: personDeleteSubject(personDeletes, successor?.name ?? null), phrase: t('modal.deletePhrase') }, ...(awaitsSuccessor ? { disabled: true, disabledReason: t('personDelete.successorPick') } : {}) } } : {})}
    >
      <div data-edit-record={recordType}>
        {/* A failed count read offers Retry beside its sentence, so Delete can come back without closing the window (T3204). */}
        {recordType === 'CompanyRecord' && deletePreview.state.kind === 'failed' ? <div data-company-delete-refusal="true"><p>{t('companyDelete.previewFailed')}</p><Button size={Size.Small} color={Color.Navy} text={[t('async.retry')]} actions={[deletePreview.reload]} /></div> : null}
        {recordType === 'PersonRecord' && personPreview.state.kind === 'failed' ? <div data-person-delete-refusal="true"><p>{t('personDelete.previewFailed')}</p><Button size={Size.Small} color={Color.Navy} text={[t('async.retry')]} actions={[personPreview.reload]} /></div> : null}
        {personFacts?.refusal ? <p data-person-delete-refusal="true">{t('personDelete.refusalLabel')} {personFacts.refusal}</p> : null}
        {open && personDeletes && openDeals > 0 ? (
          <div data-person-delete-successor="true">
            {/* 🔒 The hint and the field stand in the frame's own field layout, so the label never touches the sentence above it (T3461). */}
            <FormFields>
              <p>{fill(t('personDelete.dealsHint'), { name: personDeletes.displayName, count: personDeleteDeals(openDeals) })}</p>
              <Dropdown size={Size.Medium} label={t('personDelete.successor')} entityLabel={t('personDelete.successorEntity')} search={staffSearch} value={successor?.name ?? null} onSelect={(row) => setSuccessor({ id: row.id, name: row.line1 })} onAddNew={null} />
            </FormFields>
          </div>
        ) : null}
        {request ? <p data-edit-change-request="true">{request.notice}</p> : null}
        <RecordFields recordType={recordType} read={read} onChange={set} apiBaseUrl={apiBaseUrl} mode="edit" recordId={recordId} recordLabel={typeof initial.displayName === 'string' ? initial.displayName : null} only={only} />
        {/* 🔒 Decision 12660: Look Up Manufacturer on a Good's edit window; it writes to the Item itself, so it waits until nothing is typed here. */}
        {open && recordType === 'Item' && read('type') !== 'Service' ? (
          <LookUpManufacturerButton
            itemId={recordId}
            manufacturer={typeof initial.manufacturerCompanyIdLabel === 'string' ? initial.manufacturerCompanyIdLabel : null}
            partNumber={typeof read('manufacturerPartNumber') === 'string' ? (read('manufacturerPartNumber') as string) : null}
            apiBaseUrl={apiBaseUrl}
            onWrote={() => { onSaved(); onCancel(); }}
            disabledReason={typed ? t('itemLookup.lookUp.savedFirst') : null}
          />
        ) : null}
        {open && drawsOtherNames ? <CompanyOtherNames names={otherNames ?? namesHeld} onChange={setOtherNames} /> : null}
        {open && recordType === 'LocationRecord' ? (
          <LocationVerifyStep
            address={addressFromFields(read)}
            apiBaseUrl={apiBaseUrl}
            onAccept={(normalized) => { set('addressLines', normalized.addressLines); set('locality', normalized.locality ?? ''); if (normalized.municipality) set('municipality', normalized.municipality); set('administrativeArea', normalized.administrativeArea ?? ''); if (normalized.county) set('county', normalized.county); set('postalCode', normalized.postalCode ?? ''); }}
            onAnswered={setLanding}
          />
        ) : null}
        {open && floorRefused ? (
          <div data-floor-override="true">
            <p>{t('deal.edit.floorHint')}</p>
            <TextInput size={Size.Large} label={t('deal.edit.floorReason')} value={floorReason} onChange={setFloorReason} name="floorOverrideReason" />
          </div>
        ) : null}
      </div>
      {deceased ? (
        <ConfirmationDialog
          open
          title={t('deceased.title')}
          consequence={deceased}
          confirmLabel={t('deceased.confirm')}
          onConfirm={() => { setDeceased(null); void save(true); }}
          onCancel={() => setDeceased(null)}
        />
      ) : null}
      {typeAsk.offer ? (
        <SiteTypeAnswerWindow
          key={typeAsk.offer.siteTypeId}
          offer={typeAsk.offer}
          onCancel={typeAsk.close}
          onApply={(answers) => {
            typeAsk.close();
            void save(false, answers);
          }}
        />
      ) : null}
    </FormFrame>
  );
}

/** The value a field holds: the draft where one was typed, the record's own otherwise. */
function key(name: string, values: Record<string, unknown>, initial: Readonly<Record<string, unknown>>): unknown {
  return name in values ? values[name] : initial[name];
}
